/**
 * CorrectClientDetailsButton — small pencil icon that opens a modal to
 * correct a client's contact details (email / phone / mobile / address).
 *
 * Backed by POST /api/onboarding/correct-client/{matter_ref} (see
 * backend/hub/onboarding.py:3547). Identity columns (name / signature /
 * TOB) are deliberately NOT exposed — the backend rejects writes to
 * primary_client.full_name from this endpoint.
 *
 * Used by PipelineTrackerCore rows so Liezel / fee earners / directors
 * can fix client contact details from the tracker without drilling into
 * the matter detail page.
 */
import { useState } from "react";
import axios from "axios";
import { Pencil, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

function _formatError(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

function _currentUser() {
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "unknown"
  );
}

export default function CorrectClientDetailsButton({
  matterRef,
  clientName,
  initial,
  onSaved,
  buttonClassName,
  iconClassName,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: initial?.email || "",
    phone: initial?.phone || "",
    mobile: initial?.mobile || "",
    address: initial?.address || "",
    correction_reason: "",
  });

  const reset = () => {
    setForm({
      email: initial?.email || "",
      phone: initial?.phone || "",
      mobile: initial?.mobile || "",
      address: initial?.address || "",
      correction_reason: "",
    });
  };

  const handleOpenChange = (next) => {
    if (saving) return;
    if (next) reset();
    setOpen(next);
  };

  const handleSave = async () => {
    if (!matterRef) {
      toast.error("Matter reference is required");
      return;
    }
    const reason = form.correction_reason.trim();
    if (!reason) {
      toast.error("Correction reason is required for audit trail");
      return;
    }
    const hasAnyField =
      form.email.trim() || form.phone.trim() || form.mobile.trim() || form.address.trim();
    if (!hasAnyField) {
      toast.error("Enter at least one corrected contact field");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        // Only send fields the user actually filled in — empty strings
        // would not overwrite (backend skips None) but sending undefined
        // is cleaner.
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.mobile.trim() ? { mobile: form.mobile.trim() } : {}),
        ...(form.address.trim() ? { address: form.address.trim() } : {}),
        correction_reason: reason,
        corrected_by: _currentUser(),
        client_name: clientName || "",
      };
      const res = await axios.post(
        `${API}/onboarding/correct-client/${encodeURIComponent(matterRef)}`,
        payload,
      );
      toast.success(res?.data?.message || "Client details updated");
      setOpen(false);
      if (typeof onSaved === "function") onSaved(matterRef);
    } catch (err) {
      toast.error(_formatError(err, "Failed to update client details"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
          reset();
        }}
        title="Correct client contact details"
        data-testid={testId || "correct-client-btn"}
        className={
          buttonClassName ||
          "inline-flex items-center justify-center text-status-amber/70 hover:text-status-amber transition-colors"
        }
        style={{
          background: "transparent",
          border: "none",
          padding: 2,
          cursor: "pointer",
          lineHeight: 0,
        }}
      >
        <Pencil className={iconClassName || "w-3.5 h-3.5"} />
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Correct Client Details
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500">
              {clientName ? <><strong>{clientName}</strong> · </> : null}
              {matterRef}
            </div>
            <p className="text-[11px] text-amber-700">
              Edits email / phone / mobile / address. Name, signature and
              TOB are not editable here. All changes are audit-logged.
            </p>

            <div>
              <label className="text-xs font-medium block mb-1">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="client@example.com"
                data-testid="correct-client-email"
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium block mb-1">Phone</label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="0207..."
                  data-testid="correct-client-phone"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Mobile</label>
                <Input
                  value={form.mobile}
                  onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
                  placeholder="07..."
                  data-testid="correct-client-mobile"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">Address</label>
              <Input
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="1 Example Street, London..."
                data-testid="correct-client-address"
              />
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">
                Reason for correction <span className="text-red-500">*</span>
              </label>
              <Input
                value={form.correction_reason}
                onChange={(e) =>
                  setForm((f) => ({ ...f, correction_reason: e.target.value }))
                }
                placeholder="e.g. client supplied updated mobile"
                data-testid="correct-client-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="correct-client-save"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              )}
              Save correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
